/**
 * WEB TV — Centre multimédia officiel du Front Uni pour la RDC
 * -----------------------------------------------------------------
 * Refonte complète : ce n'est plus une "page vidéos" mais un véritable
 * média digital immersif qui raconte l'histoire du Front Uni à partir
 * de tous les contenus déjà publiés sur la plateforme (auto-agrégés).
 *
 * Sections (dans l'ordre) :
 *  1. HERO CINÉMATOGRAPHIQUE — mosaïque animée + bouton "Découvrir"
 *  2. NOTRE VISION — texte manifeste
 *  3. À LA UNE — vidéo/actualité vedette
 *  4. CONFÉRENCES — Web TV catégorie
 *  5. INTERVIEWS & REPORTAGES
 *  6. COMMUNIQUÉS OFFICIELS (auto depuis News)
 *  7. ÉVÉNEMENTS À VENIR + PASSÉS (auto depuis Events)
 *  8. KIT MÉDIA — agrégation transversale filtrable
 *  9. CTA "Rejoindre le mouvement"
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Play, Sparkles, ArrowRight, Tv, Newspaper, CalendarDays, Megaphone,
  Camera, FileText, Video as VideoIcon, Filter, Search as SearchIcon,
  Star, Pin, Eye, MapPin, X, Mic, Presentation, MessagesSquare,
  Archive as ArchiveIcon, Image as ImageIcon, Download, ExternalLink,
} from "lucide-react";
import api from "../lib/api";
import Logo from "../components/Logo";
import PresentationMode from "../components/PresentationMode";

const fmt = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); } catch { return d; } };

const KIT_FILTERS = [
  { k: "", l: "Tout", Icon: Sparkles },
  { k: "video", l: "Vidéos", Icon: VideoIcon },
  { k: "photo", l: "Photos", Icon: Camera },
  { k: "article", l: "Articles", Icon: Newspaper },
  { k: "communique", l: "Communiqués", Icon: Megaphone },
  { k: "event", l: "Événements", Icon: CalendarDays },
  { k: "document", l: "Documents", Icon: FileText },
];

export default function WebTVPage() {
  const [hub, setHub] = useState(null);
  const [kit, setKit] = useState([]);
  const [kitFilter, setKitFilter] = useState("");
  const [kitSearch, setKitSearch] = useState("");
  const [playing, setPlaying] = useState(null); // vidéo en lecteur modal
  const [showPresentation, setShowPresentation] = useState(false);

  useEffect(() => {
    api.get("/webtv/media-hub").then((r) => setHub(r.data)).catch(() => setHub({}));
    api.get("/webtv/media-kit?limit=100").then((r) => setKit(r.data)).catch(() => setKit([]));
  }, []);

  const kitFiltered = useMemo(() => {
    return kit.filter((it) => {
      if (kitFilter && it.kind !== kitFilter) return false;
      if (kitSearch) {
        const s = kitSearch.toLowerCase();
        return ((it.title || "").toLowerCase().includes(s)
          || (it.description || "").toLowerCase().includes(s)
          || (it.category || "").toLowerCase().includes(s));
      }
      return true;
    });
  }, [kit, kitFilter, kitSearch]);

  if (!hub) return <div className="min-h-screen bg-black" />;

  return (
    <div data-testid="webtv-page" className="bg-black">
      {/* =========== HERO CINÉMATOGRAPHIQUE =========== */}
      <HeroCinematic hub={hub} onPresent={() => setShowPresentation(true)} onPlayVideo={setPlaying} />

      {/* =========== NOTRE VISION =========== */}
      <VisionManifesto />

      {/* =========== À LA UNE =========== */}
      {hub.featured_video && (
        <FeaturedSection video={hub.featured_video} onPlay={() => setPlaying(hub.featured_video)} />
      )}

      {/* =========== CONFÉRENCES =========== */}
      {hub.conferences?.length > 0 && (
        <VideoRow title="Conférences" icon={Presentation} subtitle="Prises de parole institutionnelles"
                  items={hub.conferences} onPlay={setPlaying} testid="webtv-conferences" />
      )}

      {/* =========== INTERVIEWS =========== */}
      {hub.interviews?.length > 0 && (
        <VideoRow title="Interviews & débats" icon={Mic} subtitle="La parole aux acteurs"
                  items={hub.interviews} onPlay={setPlaying} dark testid="webtv-interviews" />
      )}

      {/* =========== COMMUNIQUÉS OFFICIELS =========== */}
      {hub.communiques?.length > 0 && (
        <CommuniquesSection items={hub.communiques} />
      )}

      {/* =========== ACTUALITÉS RÉCENTES =========== */}
      {hub.recent_news?.length > 0 && (
        <RecentNewsSection items={hub.recent_news} />
      )}

      {/* =========== ÉVÉNEMENTS =========== */}
      {(hub.upcoming_events?.length > 0 || hub.past_events?.length > 0) && (
        <EventsShowcase upcoming={hub.upcoming_events} past={hub.past_events} />
      )}

      {/* =========== KIT MÉDIA (intégré) =========== */}
      <MediaKitSection
        items={kitFiltered}
        filter={kitFilter} setFilter={setKitFilter}
        search={kitSearch} setSearch={setKitSearch}
        stats={hub.stats}
      />

      {/* =========== CTA FINAL =========== */}
      <CtaSection onPresent={() => setShowPresentation(true)} />

      {/* Lecteur vidéo modal */}
      {playing && <VideoPlayer video={playing} onClose={() => setPlaying(null)} />}

      {/* Mode Présentation immersif */}
      {showPresentation && <PresentationMode onClose={() => setShowPresentation(false)} />}
    </div>
  );
}

/* ============ HERO CINÉMATOGRAPHIQUE ============ */
function HeroCinematic({ hub, onPresent, onPlayVideo }) {
  const pool = (hub.hero_pool || []).filter((p) => p.image);
  // On dédoublonne pour éviter deux fois la même image
  const seen = new Set();
  const unique = pool.filter((p) => { if (seen.has(p.image)) return false; seen.add(p.image); return true; });
  // On complète si moins de 9 images
  const tiles = unique.slice(0, 9);

  return (
    <section className="relative min-h-[92vh] bg-black overflow-hidden text-white grain" data-testid="webtv-hero">
      {/* Mosaïque de fond */}
      <div className="absolute inset-0">
        <div className="w-full h-full animate-mosaic">
          {tiles.length > 0 ? (
            <div className="grid grid-cols-3 grid-rows-3 w-[110%] h-[110%] gap-1 -translate-x-[3%] -translate-y-[3%]">
              {tiles.map((t, i) => (
                <div key={i} className="relative overflow-hidden animate-tile-cycle" style={{ animationDelay: `${i * 0.4}s` }}>
                  <img src={t.image} alt="" className="w-full h-full object-cover" loading="eager" />
                </div>
              ))}
              {/* Si moins de 9, on remplit avec des blocs sombres */}
              {[...Array(Math.max(0, 9 - tiles.length))].map((_, i) => (
                <div key={`f-${i}`} className="bg-ink" />
              ))}
            </div>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-ink via-black to-ink" />
          )}
        </div>
        {/* Voiles */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/70" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(212,175,55,0.15),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_75%,rgba(200,16,46,0.12),transparent_55%)]" />
      </div>

      {/* Lignes fines */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" />
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-40" />

      {/* Contenu Hero */}
      <div className="relative min-h-[92vh] flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 w-full">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8 animate-scene-in">
              <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-3">
                <span className="w-8 h-px bg-gold" />
                <Tv className="w-3.5 h-3.5" /> WEB TV — LE MÉDIA OFFICIEL
              </div>
              <h1 className="font-serif mt-8 text-5xl sm:text-6xl lg:text-8xl leading-[0.98] tracking-tight">
                Le média <span className="text-gold italic font-light">du Front Uni.</span>
              </h1>
              <p className="mt-8 text-white/85 text-lg lg:text-xl leading-relaxed max-w-2xl">
                Conférences, communiqués, mobilisations, interviews. Toute l'action de la diaspora
                congolaise en une expérience immersive.
              </p>

              {/* Bouton principal DÉCOUVRIR */}
              <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <button
                  onClick={onPresent}
                  data-testid="webtv-discover-cta"
                  className="group relative inline-flex items-center gap-4 pl-2 pr-6 py-2 bg-gold text-ink rounded-full font-semibold shadow-[0_10px_40px_-8px_rgba(212,175,55,0.55)] hover:shadow-[0_15px_50px_-5px_rgba(212,175,55,0.75)] transition-all duration-500 hover:scale-[1.02]"
                >
                  <span className="w-12 h-12 rounded-full bg-ink text-gold grid place-items-center group-hover:scale-110 transition-transform">
                    <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                  </span>
                  <span className="uppercase tracking-[0.2em] text-sm">Découvrir le Front Uni</span>
                  <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </button>
                {hub.featured_video && (
                  <button onClick={() => onPlayVideo(hub.featured_video)} className="text-white/85 hover:text-gold text-sm inline-flex items-center gap-2 font-semibold" data-testid="webtv-hero-play-featured">
                    <Play className="w-4 h-4" fill="currentColor" /> Regarder la vidéo à la une
                  </button>
                )}
              </div>

              {/* Stats */}
              <div className="mt-14 flex items-center gap-10 lg:gap-12">
                <HeroStat n={hub.stats.videos} l="Vidéos" />
                <HeroStat n={hub.stats.articles} l="Publications" />
                <HeroStat n={hub.stats.events} l="Événements" />
                <HeroStat n={hub.stats.photos} l="Photos" />
              </div>
            </div>

            {/* Vidéo featured à droite (miniature) */}
            <div className="lg:col-span-4 hidden lg:block">
              {hub.featured_video ? (
                <button onClick={() => onPlayVideo(hub.featured_video)} className="group block w-full text-left animate-scene-in" style={{ animationDelay: "300ms" }}>
                  <div className="relative aspect-video rounded-sm overflow-hidden border border-gold/25 shadow-2xl">
                    <img src={hub.featured_video.thumbnail} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-black/40 grid place-items-center">
                      <span className="w-16 h-16 rounded-full bg-gold text-ink grid place-items-center animate-pulse-soft">
                        <Play className="w-7 h-7 ml-0.5" fill="currentColor" />
                      </span>
                    </div>
                    <div className="absolute top-2 left-2">
                      <span className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-widest bg-gold text-ink px-2 py-0.5 rounded font-bold">
                        <Star className="w-2.5 h-2.5" /> À la une
                      </span>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black to-transparent">
                      <div className="text-[0.65rem] uppercase tracking-widest text-gold">{hub.featured_video.category}</div>
                      <div className="font-serif text-base text-white leading-tight mt-1 line-clamp-2">{hub.featured_video.title}</div>
                    </div>
                  </div>
                </button>
              ) : (
                <div className="animate-scene-in text-white/70 text-sm border border-dashed border-white/15 rounded-sm p-8 text-center">
                  <Tv className="w-8 h-8 opacity-30 mx-auto mb-3" />
                  <p>Aucune vidéo à la une pour l'instant.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Indicateur de scroll */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-[0.65rem] uppercase tracking-[0.3em] flex flex-col items-center gap-2">
        <span>Faire défiler</span>
        <span className="w-px h-8 bg-gradient-to-b from-gold to-transparent" />
      </div>
    </section>
  );
}

function HeroStat({ n, l }) {
  return (
    <div>
      <div className="font-serif text-4xl text-gold">{n ?? 0}</div>
      <div className="text-white/55 text-[0.65rem] uppercase tracking-widest mt-1">{l}</div>
    </div>
  );
}

/* ============ VISION MANIFESTE ============ */
function VisionManifesto() {
  return (
    <section className="relative bg-gradient-to-br from-black via-ink to-black py-24 lg:py-32 border-y border-gold/15" data-testid="webtv-vision">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.08),transparent_60%)]" />
      <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
        <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-3">
          <span className="w-8 h-px bg-gold" /> Manifeste
        </div>
        <h2 className="font-serif mt-6 text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight">
          <span className="italic text-gold">« </span>
          Au-delà des États, la société civile congolaise et sa diaspora peuvent
          <span className="text-gold italic font-light"> peser</span> dans les espaces institutionnels internationaux.
          <span className="italic text-gold"> »</span>
        </h2>
        <div className="mt-8 h-px w-16 bg-gold mx-auto" />
        <div className="mt-6 eyebrow eyebrow-on-dark">— Dr Audrey Mundine Kabeya · Présidente</div>

        <div className="mt-16 grid sm:grid-cols-3 gap-8 text-left">
          {[
            { t: "Notre vision", d: "Une diaspora unie qui pèse sur les décisions institutionnelles européennes et internationales concernant la RDC." },
            { t: "Notre mission", d: "Documenter, structurer et amplifier la voix congolaise à travers un plaidoyer méthodique et des actions concrètes." },
            { t: "Notre engagement", d: "Solidarité active envers les victimes, justice, protection des civils et souveraineté nationale." },
          ].map((it, i) => (
            <div key={i} className="border-l-2 border-gold/60 pl-6">
              <div className="eyebrow text-gold">{it.t}</div>
              <p className="mt-3 text-white/75 leading-relaxed">{it.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ FEATURED ============ */
function FeaturedSection({ video, onPlay }) {
  return (
    <section className="relative bg-black py-24 lg:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(212,175,55,0.10),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
        <button onClick={onPlay} className="lg:col-span-7 group block text-left" data-testid="webtv-featured-block">
          <div className="relative aspect-video rounded-sm overflow-hidden border border-gold/25 shadow-2xl">
            <img src={video.thumbnail} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors" />
            <div className="absolute inset-0 grid place-items-center">
              <span className="w-24 h-24 rounded-full bg-gold text-ink grid place-items-center transition-transform duration-500 group-hover:scale-110">
                <Play className="w-11 h-11 ml-1" fill="currentColor" />
              </span>
            </div>
          </div>
        </button>
        <div className="lg:col-span-5 text-white">
          <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-3">
            <span className="w-8 h-px bg-gold" /> À la une · {video.category}
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl mt-6 leading-tight">{video.title}</h2>
          {video.description && <p className="mt-6 text-white/75 leading-relaxed">{video.description}</p>}
          <button onClick={onPlay} className="mt-8 btn-gold" data-testid="webtv-featured-play">
            Lancer la vidéo <Play className="w-4 h-4" fill="currentColor" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ============ VIDEO ROW ============ */
function VideoRow({ title, subtitle, icon: Icon, items, onPlay, dark, testid }) {
  return (
    <section className={`py-20 lg:py-24 ${dark ? "bg-gradient-to-br from-ink via-black to-ink" : "bg-black"} border-t border-white/5`} data-testid={testid}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-2">
              {Icon && <Icon className="w-3.5 h-3.5" />} {items.length} vidéo{items.length > 1 ? "s" : ""}
            </div>
            <h2 className="font-serif text-3xl lg:text-5xl mt-3 text-white leading-tight tracking-tight">{title}</h2>
            {subtitle && <p className="text-white/60 text-sm mt-2 max-w-xl">{subtitle}</p>}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((v) => (
            <VideoCard key={v.id} v={v} onPlay={() => onPlay(v)} />
          ))}
        </div>
      </div>
    </section>
  );
}

function VideoCard({ v, onPlay }) {
  return (
    <button onClick={onPlay} className="group text-left bg-white/[0.02] hover:bg-white/[0.05] border border-white/10 hover:border-gold/40 rounded-sm overflow-hidden transition-all"
            data-testid={`webtv-card-${v.id}`}>
      <div className="relative aspect-video overflow-hidden bg-ink">
        <img src={v.thumbnail} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-ink/30 group-hover:bg-ink/50 transition-colors" />
        <div className="absolute inset-0 grid place-items-center">
          <span className="w-14 h-14 rounded-full bg-white/90 text-ink grid place-items-center transition-transform duration-500 group-hover:scale-110">
            <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
          </span>
        </div>
        {v.duration && <span className="absolute bottom-2 right-2 bg-black/85 text-white text-[0.65rem] px-1.5 py-0.5 rounded font-mono">{v.duration}</span>}
      </div>
      <div className="p-4">
        <div className="eyebrow text-gold text-[0.62rem]">{v.category} · {fmt(v.published_at)}</div>
        <h3 className="font-serif text-lg text-white leading-snug mt-2 line-clamp-2">{v.title}</h3>
      </div>
    </button>
  );
}

/* ============ COMMUNIQUÉS ============ */
function CommuniquesSection({ items }) {
  return (
    <section className="relative bg-gradient-to-b from-black via-black to-crimson/10 py-24 border-t border-crimson/15" data-testid="webtv-communiques">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div>
            <div className="eyebrow inline-flex items-center gap-2 text-crimson">
              <Megaphone className="w-3.5 h-3.5" /> {items.length} communiqué{items.length > 1 ? "s" : ""}
            </div>
            <h2 className="font-serif text-3xl lg:text-5xl text-white mt-3 tracking-tight">Communiqués officiels</h2>
            <p className="text-white/60 text-sm mt-2 max-w-xl">Les positions publiques du Front Uni pour la RDC.</p>
          </div>
          <Link to="/actualites" className="text-gold hover:text-white text-sm font-semibold border-b border-gold/60 pb-1 inline-flex items-center gap-2">
            Tous les communiqués <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((n) => (
            <Link key={n.id} to={`/actualites/${n.slug}`} className="group block bg-black/60 border-l-4 border-crimson rounded-sm p-6 hover:border-gold transition-all">
              <div className="eyebrow text-crimson text-[0.62rem]">Communiqué · {fmt(n.published_at)}</div>
              <h3 className="font-serif text-xl text-white leading-tight mt-3 line-clamp-3">{n.title}</h3>
              <p className="mt-3 text-white/65 text-sm line-clamp-3">{n.excerpt}</p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-gold text-xs font-semibold group-hover:gap-3 transition-all">
                Lire le communiqué <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ RECENT NEWS ============ */
function RecentNewsSection({ items }) {
  const [featured, ...rest] = items;
  if (!featured) return null;
  return (
    <section className="bg-black py-24 border-t border-white/5" data-testid="webtv-recent-news">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div>
            <div className="eyebrow eyebrow-on-dark">Publications récentes</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-white mt-3 tracking-tight">Dernières actualités</h2>
          </div>
          <Link to="/actualites" className="btn-outline text-sm border-gold text-gold hover:bg-gold hover:text-ink">
            Voir tout <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid lg:grid-cols-12 gap-6">
          <Link to={`/actualites/${featured.slug}`} className="lg:col-span-7 group block bg-white/[0.03] border border-white/10 rounded-sm overflow-hidden hover:border-gold/40 transition-all">
            <div className="aspect-[16/10] overflow-hidden bg-ink">
              {featured.cover_image && <img src={featured.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
            </div>
            <div className="p-6 text-white">
              <div className="eyebrow text-gold text-[0.62rem]">{featured.category} · {fmt(featured.published_at)}</div>
              <h3 className="font-serif text-2xl lg:text-3xl mt-3 leading-tight">{featured.title}</h3>
              <p className="mt-3 text-white/70 line-clamp-2">{featured.excerpt}</p>
            </div>
          </Link>
          <div className="lg:col-span-5 grid gap-4">
            {rest.slice(0, 4).map((n) => (
              <Link key={n.id} to={`/actualites/${n.slug}`} className="group flex gap-4 bg-white/[0.02] hover:bg-white/[0.06] border border-white/10 hover:border-gold/40 rounded-sm p-3 transition-all">
                <div className="w-24 h-20 shrink-0 overflow-hidden rounded-sm bg-ink">
                  {n.cover_image && <img src={n.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />}
                </div>
                <div className="min-w-0 text-white">
                  <div className="eyebrow text-gold text-[0.6rem]">{n.category}</div>
                  <div className="font-serif text-sm text-white leading-tight mt-1 line-clamp-2">{n.title}</div>
                  <div className="text-[0.65rem] text-white/45 mt-1">{fmt(n.published_at)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ EVENTS SHOWCASE ============ */
function EventsShowcase({ upcoming, past }) {
  return (
    <section className="bg-gradient-to-br from-ink via-black to-ink py-24 border-t border-white/5" data-testid="webtv-events">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-16">
        {upcoming?.length > 0 && (
          <div>
            <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-2">
              <CalendarDays className="w-3.5 h-3.5" /> Mobilisation à venir
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl text-white mt-3 tracking-tight">Prochains rendez-vous</h2>
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              {upcoming.slice(0, 3).map((ev) => (
                <Link key={ev.id} to={`/evenements/${ev.slug}`} className="group bg-white/[0.03] hover:bg-white/[0.07] border border-white/10 hover:border-gold/40 rounded-sm p-6 transition-all">
                  <div className="eyebrow text-gold text-[0.62rem]">{fmt(ev.date)}</div>
                  <h3 className="font-serif text-xl text-white mt-3 leading-tight">{ev.title}</h3>
                  <div className="mt-4 flex items-center gap-2 text-white/70 text-sm"><MapPin className="w-4 h-4 text-gold" /> {ev.hide_location ? "Communiqué après inscription" : `${ev.city}, ${ev.country}`}</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {past?.length > 0 && (
          <div>
            <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-2">
              <ArchiveIcon className="w-3.5 h-3.5" /> Mémoire vivante
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl text-white mt-3 tracking-tight">Événements passés</h2>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {past.slice(0, 6).map((ev) => (
                <Link key={ev.id} to={`/evenements/${ev.slug}`} className="group bg-white/[0.03] border border-white/10 rounded-sm overflow-hidden hover:border-gold/40 transition-all">
                  <div className="aspect-video bg-ink overflow-hidden">
                    {(ev.images?.[0] || ev.banner_image) && (
                      <img src={ev.images?.[0] || ev.banner_image} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                    )}
                  </div>
                  <div className="p-4 text-white">
                    <div className="eyebrow text-gold text-[0.6rem]">{fmt(ev.date)}</div>
                    <div className="font-serif text-sm text-white mt-1 line-clamp-2">{ev.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ============ KIT MÉDIA ============ */
function MediaKitSection({ items, filter, setFilter, search, setSearch, stats }) {
  return (
    <section className="bg-black py-24 lg:py-28 border-t border-gold/15" data-testid="webtv-kit-media">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Kit média — mis à jour automatiquement
            </div>
            <h2 className="font-serif text-3xl lg:text-5xl text-white mt-3 tracking-tight">
              Toutes nos ressources <span className="text-gold italic font-light">en un seul endroit.</span>
            </h2>
            <p className="text-white/60 text-sm mt-3 max-w-xl">
              Chaque contenu publié sur la plateforme (vidéo, actualité, communiqué, événement, photo)
              apparaît automatiquement ici. Aucune double saisie.
            </p>
          </div>
        </div>

        {/* Filtres */}
        <div className="flex items-center gap-4 flex-wrap mb-8">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…"
                   className="w-full bg-white/[0.05] border border-white/15 rounded-sm pl-10 pr-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-gold outline-none"
                   data-testid="kit-search" />
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none py-1 flex-1">
            {KIT_FILTERS.map((f) => (
              <button key={f.k} onClick={() => setFilter(f.k)}
                      className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors inline-flex items-center gap-1.5 ${
                        filter === f.k ? "bg-gold text-ink" : "bg-white/[0.05] border border-white/15 text-white/75 hover:border-gold/50"
                      }`}
                      data-testid={`kit-filter-${f.k || "all"}`}>
                <f.Icon className="w-3.5 h-3.5" /> {f.l}
              </button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <div className="py-16 text-center text-white/50 border border-dashed border-white/15 rounded-sm">
            <ImageIcon className="w-8 h-8 opacity-40 mx-auto mb-2" />
            Aucune ressource trouvée avec ces critères.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="kit-grid">
            {items.map((it, i) => (
              <KitCard key={`${it.kind}-${it.id}-${i}`} it={it} />
            ))}
          </div>
        )}

        {/* Stats bas */}
        <div className="mt-14 pt-8 border-t border-white/10 grid grid-cols-2 sm:grid-cols-5 gap-6 text-center">
          {[
            { n: stats?.videos, l: "Vidéos" },
            { n: stats?.articles, l: "Articles" },
            { n: stats?.communiques, l: "Communiqués" },
            { n: stats?.events, l: "Événements" },
            { n: stats?.photos, l: "Photos" },
          ].map((s, i) => (
            <div key={i}>
              <div className="font-serif text-3xl text-gold">{s.n ?? 0}</div>
              <div className="text-white/55 text-[0.65rem] uppercase tracking-widest mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function KitCard({ it }) {
  const badge = {
    video: { l: "Vidéo", cls: "bg-gold text-ink" },
    photo: { l: "Photo", cls: "bg-white/20 text-white" },
    article: { l: "Article", cls: "bg-white/20 text-white" },
    communique: { l: "Communiqué", cls: "bg-crimson text-white" },
    event: { l: "Événement", cls: "bg-white/20 text-white" },
    document: { l: "Document", cls: "bg-white/20 text-white" },
  }[it.kind] || { l: it.kind, cls: "bg-white/20 text-white" };

  const isExternal = it.detail_url?.startsWith("http");
  const linkProps = it.detail_url ? {
    href: it.detail_url,
    ...(isExternal ? { target: "_blank", rel: "noreferrer" } : {}),
  } : null;

  const inner = (
    <div className="relative aspect-square bg-white/[0.03] rounded-sm overflow-hidden border border-white/10 group-hover:border-gold/40 transition-all">
      {it.image ? (
        <img src={it.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
      ) : (
        <div className="w-full h-full grid place-items-center text-white/25">
          {it.kind === "document" ? <FileText className="w-8 h-8" /> :
           it.kind === "communique" ? <Megaphone className="w-8 h-8" /> :
           <ImageIcon className="w-8 h-8" />}
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="absolute top-2 left-2">
        <span className={`text-[0.6rem] uppercase tracking-widest px-2 py-0.5 rounded font-semibold ${badge.cls}`}>{badge.l}</span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-3 text-white">
        <div className="font-serif text-sm leading-tight line-clamp-2">{it.title || it.category}</div>
        {it.date && <div className="text-[0.65rem] text-gold mt-1">{fmt(it.date)}</div>}
      </div>
      {linkProps && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="bg-gold text-ink p-1.5 rounded-sm inline-block"><ExternalLink className="w-3 h-3" /></span>
        </div>
      )}
    </div>
  );

  if (linkProps && isExternal) {
    return <a {...linkProps} className="group block" data-testid={`kit-item-${it.kind}-${it.id}`}>{inner}</a>;
  }
  if (linkProps) {
    return <Link to={linkProps.href} className="group block" data-testid={`kit-item-${it.kind}-${it.id}`}>{inner}</Link>;
  }
  return <div className="group block" data-testid={`kit-item-${it.kind}-${it.id}`}>{inner}</div>;
}

/* ============ CTA ============ */
function CtaSection({ onPresent }) {
  return (
    <section className="relative bg-gradient-to-br from-ink via-black to-ink py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.14),transparent_60%)]" />
      <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
        <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-3 justify-center">
          <span className="w-8 h-px bg-gold" /> Vous voulez tout comprendre en 1 minute ?
        </div>
        <h2 className="font-serif mt-6 text-4xl lg:text-6xl text-white leading-tight tracking-tight">
          Regardez notre <span className="text-gold italic font-light">présentation immersive.</span>
        </h2>
        <p className="mt-6 text-white/70 text-lg max-w-2xl mx-auto">
          Une expérience cinématographique construite automatiquement à partir de nos vraies actions, vidéos et communiqués.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-5">
          <button onClick={onPresent} className="btn-gold text-base" data-testid="webtv-cta-present">
            <Play className="w-4 h-4" fill="currentColor" /> Découvrir le Front Uni
          </button>
          <Link to="/adhesion" className="text-white/85 hover:text-gold inline-flex items-center gap-2 font-semibold text-sm">
            Rejoindre le mouvement <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ============ Video Player Modal ============ */
function VideoPlayer({ video, onClose }) {
  useEffect(() => {
    if (video?.id) api.get(`/webtv/videos/${video.id}`).catch(() => {});
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [video?.id]);

  const yt = video.youtube_id;
  if (!yt) return null;

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center p-4 sm:p-6" onClick={onClose} data-testid="webtv-player">
      <div className="absolute inset-0 bg-black/95 backdrop-blur-md" />
      <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-11 right-0 text-white/85 hover:text-gold inline-flex items-center gap-2 text-sm" data-testid="webtv-player-close">
          <X className="w-4 h-4" /> Fermer
        </button>
        <div className="aspect-video rounded-sm overflow-hidden bg-black shadow-2xl border border-gold/25">
          <iframe src={`https://www.youtube.com/embed/${yt}?autoplay=1&rel=0`} title={video.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen />
        </div>
        <div className="mt-5 text-white">
          <div className="eyebrow eyebrow-on-dark">{video.category} · {fmt(video.published_at)}</div>
          <h2 className="font-serif text-2xl lg:text-3xl mt-2 leading-tight">{video.title}</h2>
          {video.description && <p className="mt-3 text-white/70 leading-relaxed max-w-3xl">{video.description}</p>}
        </div>
      </div>
    </div>
  );
}
